/**
 * Data hook for SmswiserCampaignStatsTab (drops)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useLayoutEffect, useMemo, useRef } from 'react';

import { useSmswiserCampaignDropsWithStats, useStaticTags } from '@/hooks';
import { useCountry } from '@/hooks';

import { toRangeISO } from '../../shared';
import {
  computeDropSequenceByUniqueId,
  dedupeDropsWithStatsByUniqueId,
  toGenericStatsRowFromDrop,
} from '../../table';
import type { GenericStatsRow } from '../../shared/generic-stats.types';
import { useStatsTabState } from './useStatsTabState';
import { useDropPerfTagRows } from './useDropPerfTagRows';
import { useFilteredAndSortedRowsGeneric } from '../filter-sort/useFilteredAndSortedRowsGeneric';
import { useScaleDropHandlers } from '../scale/useScaleDropHandlers';

export interface UseSmswiserCampaignStatsTabDataParams {
  countryKey: string;
  wiserIds: number[];
  workDate: string;
  onScaleAndSendComplete?: () => void;
}

export interface UseSmswiserCampaignStatsTabDataResult {
  state: ReturnType<typeof useStatsTabState>;
  scaleHandlers: ReturnType<typeof useScaleDropHandlers>;
  rows: GenericStatsRow[];
  groups: ReturnType<typeof useFilteredAndSortedRowsGeneric>['groups'];
  totals: Record<string, number>;
  allGenericRows: GenericStatsRow[];
  query: ReturnType<typeof useSmswiserCampaignDropsWithStats>;
  selectedDropsForModals: import('@/features/smswiser-campaigns/types/workspace.types').DropsWithStatsRow[];
  routes: Array<{ id: number; name: string; pricePerSms?: number | null }>;
  leadsLists: Array<{ id: number; name: string; count?: number | null }>;
  tlds: Array<{ value: string; displayName: string }>;
  returnTo: string;
  countryLoading: boolean;
  staticTagFilterOptions: string[];
  staticTagFilterColors: Record<string, string>;
  staticTagsLoading: boolean;
  tagFilterOptions: string[];
  tagFilterColors: Record<string, string> | undefined;
  /** Rule name -> emoji ("+" / "-") for the drops tag display. */
  tagEmojis: Record<string, string> | undefined;
  /** Names of rules with source_* conditions — their emoji renders bare. */
  scopedRuleNames: string[] | undefined;
  tagsLoading: boolean;
}

export function useSmswiserCampaignStatsTabData({
  countryKey,
  wiserIds,
  workDate,
  onScaleAndSendComplete,
}: UseSmswiserCampaignStatsTabDataParams): UseSmswiserCampaignStatsTabDataResult {
  const state = useStatsTabState(workDate, 'drops');
  const country = useCountry(countryKey, workDate);
  const routes = country.data?.routes ?? [];
  const leadsLists = country.data?.leadsLists ?? [];
  const tlds = country.data?.shortLinkTlds ?? [];
  const campaigns = country.data?.campaigns ?? [];
  const primaryWiserId = wiserIds[0] ?? 0;

  const scaleHandlers = useScaleDropHandlers(
    countryKey,
    primaryWiserId,
    leadsLists,
    routes,
    tlds.map((t) => ({ value: t.value, displayName: t.displayName })),
    campaigns.map((c) => ({ id: c.id, name: c.name })),
    onScaleAndSendComplete,
    (updater) => {
      state.setSelectedRows(() => (updater as (p: GenericStatsRow[]) => GenericStatsRow[])([]));
    },
    (show) => state.setShowScaleModal(show),
    (show) => state.setShowCopyAsDraftModal(show),
  );

  const range = useMemo(() => toRangeISO(state.fromDate, state.toDate), [state.fromDate, state.toDate]);
  const query = useSmswiserCampaignDropsWithStats({
    countryKey,
    wiserIds,
    from: range.from,
    to: range.to,
    start: 0,
    length: state.length,
    currency: state.currency,
  });

  const { data: staticTags, isLoading: staticTagsLoading } = useStaticTags();
  const rowsFromQuery = query.data?.rows;
  const allDropRows = useMemo(() => {
    const raw = rowsFromQuery ?? [];
    return dedupeDropsWithStatsByUniqueId(raw, workDate);
  }, [rowsFromQuery, workDate]);
  const staticTagFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const r of allDropRows) {
      const name = r.drop.staticTag?.name;
      if (name && String(name).trim()) seen.add(name);
      else seen.add('SMS');
    }
    if (seen.size === 0) seen.add('SMS');
    return [...seen].sort();
  }, [allDropRows]);
  const staticTagFilterColors = useMemo(() => {
    const map: Record<string, string> = {};
    if (staticTags?.length) {
      for (const t of staticTags) if (t.name && t.color) map[t.name] = t.color;
    }
    for (const r of allDropRows) {
      const tag = r.drop.staticTag;
      if (tag?.name && tag?.color) map[tag.name] = tag.color;
    }
    return map;
  }, [staticTags, allDropRows]);
  const dropSequenceByUniqueId = useMemo(
    () => computeDropSequenceByUniqueId(allDropRows, workDate),
    [allDropRows, workDate],
  );
  const baseGenericRows = useMemo(
    () => allDropRows.map((r) => toGenericStatsRowFromDrop(r, dropSequenceByUniqueId)),
    [allDropRows, dropSequenceByUniqueId],
  );
  const {
    rowsWithTags: allGenericRows,
    tagFilterOptions,
    tagFilterColors,
    tagEmojis,
    scopedRuleNames,
    tagsLoading,
  } = useDropPerfTagRows(countryKey, wiserIds, range, state.currency, baseGenericRows);

  const { rows, groups } = useFilteredAndSortedRowsGeneric(
    allGenericRows,
    state.filtersState,
    state.sort ?? { field: 'dropList', dir: 'desc' },
    workDate,
    state.groupBy,
  );

  const selectedDropsForModals = useMemo(
    () =>
      state.selectedRows
        .map((r) => r._originalDrop)
        .filter((d): d is NonNullable<typeof d> => !!d),
    [state.selectedRows],
  );

  const lastGroupByInitialized = useRef<typeof state.groupBy | null>(null);
  useLayoutEffect(() => {
    if (state.groupBy === 'none' || !groups || groups.length === 0) {
      if (state.groupBy === 'none') lastGroupByInitialized.current = null;
      return;
    }
    if (lastGroupByInitialized.current === state.groupBy) return;
    lastGroupByInitialized.current = state.groupBy;
    state.setCollapsedGroups((prev) => {
      const next = new Set(prev);
      for (const g of groups) {
        next.add(g.key);
        if (g.subGroups) for (const sg of g.subGroups) next.add(sg.key);
      }
      return next;
    });
  }, [state.groupBy, state.setCollapsedGroups, groups]);

  return {
    state,
    scaleHandlers,
    rows,
    groups,
    totals: query.data?.totals ?? {},
    allGenericRows,
    query,
    selectedDropsForModals,
    routes,
    leadsLists,
    tlds,
    returnTo: `/smswiser/${encodeURIComponent(countryKey)}`,
    countryLoading: country.isLoading,
    staticTagFilterOptions,
    staticTagFilterColors,
    staticTagsLoading,
    tagFilterOptions,
    tagFilterColors,
    tagEmojis,
    scopedRuleNames,
    tagsLoading,
  };
}
